"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";

import {
  DigitalReportLeadFormDrawer,
  type DigitalReportLeadFormValues,
} from "@/components/shared/DigitalReportLeadForm";
import { formatContactEmail } from "@/lib/format-contact-email";
import { parseNotifyEmailList } from "@/lib/notify-email-list";

const DIGITAL_REPORT_NOTIFY_TO = parseNotifyEmailList(
  process.env.NEXT_PUBLIC_DIGITAL_REPORT_NOTIFY_EMAIL,
);

type DigitalReportLeadContextValue = {
  open: () => void;
};

const DigitalReportLeadContext = createContext<DigitalReportLeadContextValue | null>(null);

export function useDigitalReportLead(): DigitalReportLeadContextValue {
  const ctx = useContext(DigitalReportLeadContext);
  if (!ctx) {
    throw new Error("useDigitalReportLead must be used within DigitalReportLeadProvider");
  }
  return ctx;
}

export const freeDigitalReportHeroButtonClassName =
  "inline-flex min-h-13 w-full cursor-pointer items-center justify-center rounded-2xl bg-[#ff5c00] px-10 py-4 text-lg font-semibold tracking-tight text-white transition-colors hover:bg-[#e65500] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white md:min-h-15 md:rounded-3xl md:px-14 md:py-5 md:text-xl";

export function FreeDigitalReportHeroButton({
  className = freeDigitalReportHeroButtonClassName,
  onClick,
  children = "Free Digital Report",
  ...rest
}: ComponentProps<"button">) {
  const { open } = useDigitalReportLead();

  return (
    <button
      type="button"
      {...rest}
      className={className}
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented) open();
      }}
    >
      {children}
    </button>
  );
}

export function DigitalReportLeadProvider({ children }: { children: ReactNode }) {
  const [reportFormOpen, setReportFormOpen] = useState(false);
  const [leadSubmitError, setLeadSubmitError] = useState<string | null>(null);
  const [leadSubmitSuccess, setLeadSubmitSuccess] = useState(false);

  const handleReportFormOpenChange = useCallback((open: boolean) => {
    setReportFormOpen(open);
    if (!open) {
      setLeadSubmitError(null);
    }
  }, []);

  const open = useCallback(() => {
    setReportFormOpen(true);
  }, []);

  const handleDigitalReportLead = async (
    values: DigitalReportLeadFormValues,
    recaptchaToken: string,
  ) => {
    setLeadSubmitError(null);
    if (DIGITAL_REPORT_NOTIFY_TO.length === 0) {
      setLeadSubmitError(
        "Notification email is not configured. Add NEXT_PUBLIC_DIGITAL_REPORT_NOTIFY_EMAIL to your environment.",
      );
      return;
    }

    const html = formatContactEmail(
      {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        businessName: values.businessName,
        website: values.website,
      },
      { title: "Free digital report request" },
    );

    const res = await fetch("/api/contact", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: `${values.firstName} ${values.lastName}`.trim(),
        email: values.email,
        phone: values.phone || undefined,
        recaptchaToken,
        recaptchaAction: "digital_report_submit",
        notification: {
          to:
            DIGITAL_REPORT_NOTIFY_TO.length === 1
              ? DIGITAL_REPORT_NOTIFY_TO[0]!
              : DIGITAL_REPORT_NOTIFY_TO,
          subject: `Digital report: ${values.businessName}`,
          html,
        },
      }),
    });
    const data = (await res.json()) as {
      ok?: boolean;
      email?: { ok?: boolean };
      success?: boolean;
      error?: string;
    };

    if (res.ok && data.ok === true && data.email?.ok) {
      setLeadSubmitSuccess(true);
      return;
    }

    setLeadSubmitError(
      typeof data.error === "string"
        ? data.error
        : data.success === false
          ? "Could not save your contact details. Please try again."
          : "Could not submit. Please try again.",
    );
  };

  const value = useMemo(() => ({ open }), [open]);

  return (
    <DigitalReportLeadContext.Provider value={value}>
      {children}
      <DigitalReportLeadFormDrawer
        open={reportFormOpen}
        onOpenChange={handleReportFormOpenChange}
        idPrefix="global-digital-report-drawer"
        submitError={leadSubmitError}
        submitSuccess={leadSubmitSuccess}
        onLeadSubmit={handleDigitalReportLead}
        onRecaptchaError={setLeadSubmitError}
      />
    </DigitalReportLeadContext.Provider>
  );
}
